import Vue from 'vue';
import Component from 'vue-class-component';
import { Mixins, Prop , Provide, Ref, Watch} from 'vue-property-decorator';
import { COURSE_MATERIAL_KEYS } from '@/courses/values/courses.values';
import { Status } from '@/models/status.model';
import EdPreviewFilePage from '@/courses/components/ed-preview-file-pages/ed-preview-file-page/ed-preview-file-page.vue';
import EdInputModel from '@/models/ed-input.model';
import EdFormModel from '@/models/ed-form.model';
import { required, numeric, between  } from 'vuelidate/lib/validators'
import { PreviewPageItem } from '@/courses/interfaces/preview-page-item.interface';
import ToolsProvider from '@/providers/tools';
import SectionSchoolCalendarMixin from '@/sections/mixins/section-school-calendar.mixin';
import { EdPreviewFilePagesProvider } from './ed-preview-file-pages.provider';
import KEYS from '@/values/keys';

@Component({
  name: 'ed-preview-file-pages',
  components: {
    EdPreviewFilePage
  }
})
export default class EdPreviewFilePages extends Mixins(SectionSchoolCalendarMixin) {
  @Prop() readonly url: string;
  // @Prop() readonly params: any;   //Typescript upgrade
  declare readonly params: any; 
  @Prop() readonly hasDisplayTypeOptions: boolean;
  @Prop({required: false, default: true}) readonly validateForm: boolean;

  @Ref('virtualScroller') readonly virtualScroller: any;

  @Provide()
  PreviewFilePagesProvider = new EdPreviewFilePagesProvider();

  pages: PreviewPageItem[] = [];
  selectedPages: PreviewPageItem[] = [];
  startPage: number = null;
  endPage: number = null;
  renderComponent: number = 0;
  isHomework: boolean = false;
  intitFunction: boolean = false;
  picked: string = 'CONTINUE';
  pdfStatus: Status = new Status();
  pageToGo = '';
  form: any = EdFormModel([
    EdInputModel('dueDate', '', {}),
    EdInputModel('lastDate', '', {}),
    EdInputModel('homeworkPoints', 100, {required, numeric, between: between(1, 1000)}, true),
    EdInputModel('homeworkWeight', 1, {required, numeric, between: between(1, 1000)}, true)
  ]);

  @Ref() 
  parentDivRef!: HTMLDivElement;

  get numberPages(): number {
    return this.PreviewFilePagesProvider.totalPages;
  }

  get currentPageDownloading(): number {
    return this.PreviewFilePagesProvider.currentPageDownloading;
  }

  get minPageWidth(): number {
    return this.PreviewFilePagesProvider.minPageWidth;
  }

  get product() {
    return this.params.product;
  }

  get hasGrading() {
    return this.product?.hasGrading;
  }

  get selectedPagesLength(): boolean {
    return this.selectedPages.length > 0
  }

  get pickedValue(): boolean {
    return this.picked === COURSE_MATERIAL_KEYS.SPLICE;
  }
  
  get selectedPagesIds(): any[] {
    if(this.selectedPagesLength && !this.pdfStatus.loading) {
      return this.findSelectPagesIds(this.startPage, this.endPage)
    } else {
      return [];
    }
  }

  get checkSaveValid(): boolean {
    if(this.pickedValue) {
      let valid = true;
      if(this.isHomework && this.validateForm && this.hasGrading) {
        valid = this.form.homeworkPoints.valid &&  this.form.homeworkWeight.valid;
      }
      return (ToolsProvider.hasNumber(this.startPage) && ToolsProvider.hasNumber(this.endPage) && valid) ? false : true;
    } else {
      return ToolsProvider.hasNumber(this.startPage) ? false : true;
    }
  }

  get hasBeenResolvedByAnyStudent(){
    return this.params ? this.params.hasBeenResolvedByAnyStudent : false;
  }

  get isPageSelectionChanged(): boolean {
    return this.params.displayType !== this.picked ||
      Number(this.params.start) !== (this.startPage !== null ? this.startPage + 1 : null) ||
      Number(this.params.end) !== (this.endPage !== null ? this.endPage + 1 : null);
  }

  get courseType(): string {
    return this.$store.state.CourseStore.courses.content.find(
      (course: any) => course.id === this.params.courseId
    )?.type;
  }

  get showDisclaimer(): boolean {
    return this.isPageSelectionChanged && ![KEYS.CONTENT_TYPE.COURSE_TEMPLATE, KEYS.CONTENT_TYPE.CST].includes(this.courseType);
  }

  private findSelectPagesIds(startPage: number, endPage: number): any[] {
    let pageIds: any[] = [];
    if(this.pickedValue) {
      let endPag: number = endPage ? endPage : startPage;
      for (let i = startPage; i <= endPag ; i++) { 
        pageIds.push(i);
      }
    } else {
      for (let i = startPage; i <= this.numberPages; i++) { 
        pageIds.push(i);
      }
    }
    return pageIds;   
  }

  private findSelectedPages(): any[] {
    const startPage: number = this.startPage;
    const hasStartPage = !isNaN(Number(this.startPage));
    const hasEndPage = !isNaN(Number(this.endPage));
    this.renderComponent++
    if(this.pickedValue) {
      if(hasStartPage && hasEndPage) {
        return this.pages.slice(startPage, this.endPage + 1);
      } else if(hasStartPage && !hasEndPage) {
        return this.pages.slice(startPage, this.startPage + 1);
      } else {
        return [];
      } 
    } else {
      return hasStartPage ? this.pages.slice(startPage) : [];
    }    
  }

  public selectPage(item: PreviewPageItem) {
    const numberPage = item.page._pageIndex;
    const hasStartPage = ToolsProvider.hasNumber(this.startPage);
    const hasEndPage = ToolsProvider.hasNumber(this.endPage);
    Vue.set(this.pages, numberPage, item);
    if(this.pickedValue) { 
      if(!hasStartPage || !hasEndPage) {
        if(hasStartPage && (numberPage > this.startPage)) {
          this.endPage = numberPage;
        } else {
          this.startPage = numberPage;           
          this.endPage = numberPage;
        }  
      } else {
        numberPage > this.endPage ? this.endPage = numberPage : this.startPage = numberPage;
        this.startPage === numberPage ? this.endPage = numberPage : null;
      }
    } else {
      this.startPage = numberPage;
    }
    this.selectedPages = this.findSelectedPages(); 
  }

  public goBack() {
    this.$emit('goBack');
  }

  public save() {
    let data: any = {
      start: this.startPage + 1,
      end: this.endPage + 1, 
      picked: this.picked,
      homework: this.isHomework,
      dueDate: this.isHomework ? this.form.dueDate.value : '',
      lastDate: this.isHomework ? this.form.lastDate.value : '',
      homeworkPoints: this.isHomework ? this.form.homeworkPoints.value : 0,
      homeworkWeight: this.isHomework ? this.form.homeworkWeight.value : 0
    }
    this.$emit('save', data);
  }

  public async refresh() {
    this.pdfStatus.setLoading();
    try {
      await this.startRenderPages();
    } catch(err) {
      this.pdfStatus.setError();
    }
  }

  public initForm(dueDate: string = '', lastDate: string = '', homework: boolean = false, homeworkPoints: number = 100, homeworkWeight: number = 1) {
    this.form = EdFormModel([
      EdInputModel('dueDate', dueDate, {}),
      EdInputModel('lastDate', lastDate, {}),
      EdInputModel('homeworkPoints', homeworkPoints, { numeric, between: between(1, 1000)}, true),
      EdInputModel('homeworkWeight', homeworkWeight, { numeric, between: between(1, 1000)}, true)
    ]);

    if(this.isHomework && this.hasGrading){
      this.form.setValidator('homeworkPoints', 'required', required);
      this.form.setValidator('homeworkWeight', 'required', required);
    }
  }

  async startRenderPages() {
    this.pages = await this.PreviewFilePagesProvider.setPdfPagesFromUrlFile(this.url);

    this.pdfStatus.setLoad();
  }

  async goToPage() {
    if (this.pageToGo) {
      const pageNumber = Number(this.pageToGo) - 1;

      if (pageNumber <= this.numberPages) {
        this.virtualScroller.scrollToItem(pageNumber); 
      }
    }
  }

  onPageToGoChanged(event: any) {
    event.stopPropagation();
    const cleaned = this.pageToGo.replace(/\D/g, '');
    
    this.pageToGo = cleaned;
  }

  /* Hooks */
  async mounted() {
    try {
      this.pdfStatus.setLoading();
      await this.startRenderPages();
      !this.hasDisplayTypeOptions ? this.picked = 'SPLICE' : null;
      this.initForm(this.params.dueDate, this.params.lastDate, this.params.homework, this.params.homeworkPoints, this.params.homeworkWeight);
      if(this.params.edition) {
        this.hasDisplayTypeOptions ? this.picked = this.params.displayType : null;
        this.startPage = Number(this.params.start) - 1;
        this.endPage = this.params.end ? Number(this.params.end) - 1 : null;
        setTimeout(() => { this.isHomework = this.params.homework; }, 0);
        this.selectedPages = this.findSelectedPages();
      }
      this.picked != COURSE_MATERIAL_KEYS.SPLICE ? this.intitFunction = true : null;
    } catch(err) {
      this.pdfStatus.setError();
    }    
  }

  @Watch('picked', { deep: true, immediate: false })
  changeSelectedPages() {
    /* this logic prevents that in edition the values are changed at the beginning  */
    if(!this.pdfStatus.loading && this.intitFunction) {
      this.endPage = this.pickedValue ? this.startPage : null;
      this.selectedPages = this.findSelectedPages();
    } else this.intitFunction = true;
  }

  @Watch('pickedValue')
  pickedValueChanged(value) {
    if(!value) this.isHomework = false;
  }

}